import React, { useState, useEffect, useCallback } from 'react';
import Select, { components } from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

const CustomMultiValue = (props) => {
    const { index, getValue } = props;
    const maxToShow = 2;
    const values = getValue();
    const [showPopover, setShowPopover] = React.useState(false);
    const popoverRef = React.useRef(null);
    
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setShowPopover(false);
            }
        };
        if (showPopover) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPopover]);
    
    if (index < maxToShow) {
        return <components.MultiValue {...props} />;
    }
    
    if (index === maxToShow) {
        const hiddenLabels = values.slice(maxToShow);
        return (
            <div className="relative flex items-center" style={{ margin: '2px' }} ref={popoverRef}>
                <div 
                    className="bg-slate-100 text-slate-600 text-[11px] font-bold px-2 py-1 rounded border border-slate-200 whitespace-nowrap cursor-pointer hover:bg-slate-200 transition-colors" 
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowPopover(!showPopover);
                    }}
                >
                    +{values.length - maxToShow} more
                </div>
                
                {showPopover && (
                    <div 
                        className="absolute top-full left-0 mt-1 bg-white border border-slate-200 shadow-xl rounded-lg p-2 z-[99999] min-w-[180px] max-w-[250px] max-h-[200px] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center border-b pb-1 mb-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Hidden Selections</span>
                            <button onClick={(e) => { e.stopPropagation(); setShowPopover(false); }} className="text-slate-400 hover:text-red-500 transition">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <ul className="text-xs text-slate-700 space-y-1">
                            {hiddenLabels.map((v, i) => (
                                <li key={i} className="py-0.5 truncate border-b border-slate-50 last:border-0" title={v.label}>{v.label}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    }
    
    return null;
};
import { FiUsers, FiClock, FiCheckCircle, FiChevronDown, FiSearch, FiActivity, FiCalendar, FiChevronLeft, FiChevronRight, FiRotateCcw, FiX, FiList, FiCopy, FiUserX, FiRefreshCcw, FiLogOut, FiDollarSign, FiDownload, FiInfo } from 'react-icons/fi';
import api from '../utils/api';
import TicketDetails from '../components/TicketDetails';
import { useUser } from '../contexts/UserContext';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { RiFileExcelFill, RiFilePdf2Fill } from 'react-icons/ri';

const getStatusBadgeClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'new') return 'bg-rose-50 text-rose-600 border border-rose-100';
    if (s === 'open') return 'bg-blue-50 text-blue-600 border border-blue-100';
    if (s === 'in process' || s === 'in progress') return 'bg-amber-50 text-amber-600 border border-amber-100';
    if (s === 'completed' || s.includes('approved')) return 'bg-emerald-50 text-emerald-600 border border-emerald-100';
    if (s === 'closed') return 'bg-slate-100 text-slate-500 border border-slate-200';
    return 'bg-indigo-50 text-indigo-600 border border-indigo-100';
};


const getPriorityBadgeClass = (priority) => {
    const p = (priority || '').toLowerCase();
    if (p === 'critical' || p === 'high') return 'bg-red-50 text-red-600 border border-red-100';
    if (p === 'medium') return 'bg-amber-50 text-amber-600 border border-amber-100';
    if (p === 'low') return 'bg-slate-50 text-slate-500 border border-slate-200';
    return 'bg-blue-50 text-blue-600 border border-blue-100';
};

const formatTimeTaken = (createdStr, completedStr) => {
    if (!createdStr) return '—';
    const created = new Date(createdStr);
    const end = completedStr ? new Date(completedStr) : new Date();
    const diffMs = end - created;
    if (diffMs <= 0) return '0d 0h';
    const diffHrs = diffMs / (1000 * 60 * 60);
    const days = Math.floor(diffHrs / 24);
    const hours = Math.floor(diffHrs % 24);
    return `${days}d ${hours}h`;
};


const Verticalhead_workload_overview = () => {
    const { user } = useUser();

    const priorityOptions = [
        { value: "1", label: "Critical" },
        { value: "2", label: "High" },
        { value: "3", label: "Medium" },
        { value: "7", label: "Low" },
        { value: "4", label: "To Be Done In Future" },
    ];

    const statusOptions = [
        { value: "1", label: "New" },
        { value: "4", label: "IN process" },
        { value: "3", label: "Pending verification & closure" },
        { value: "5", label: "Closed" },
        { value: "6", label: "Reopen" },
        { value: "8", label: "Testing" },
        { value: "9", label: "Testing Approved" },
        { value: "10", label: "Testing Rejected" },
        { value: "12", label: "Pending Customer Verification & Confirmation" },
        { value: "13", label: "On Hold" },
        { value: "14", label: "Code Review Request" },
        { value: "15", label: "Code Review Approved" },
        { value: "16", label: "Code Review Rejected" },
        { value: "17", label: "Production Move Request" },
        { value: "18", label: "Production Moved" },
        { value: "19", label: "Production Tested" },
    ].filter(opt => {
        if (Number(user?.role_id) === 2 && opt.value === "13") return false;
        return true;
    });

    // Date Helpers
    const getFirstDayOfMonthInputDate = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    };

    const getTodayInputDate = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const toInputDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateDDMMYYYY = (dateStr) => {
        if (!dateStr) return '';
        const cleanDate = dateStr.substring(0, 10);
        const [year, month, day] = cleanDate.split('-');
        return `${day}/${month}/${year}`;
    };

    // Date Range Picker States
    const [fromDate, setFromDate] = useState(getFirstDayOfMonthInputDate());
    const [toDate, setToDate] = useState(getTodayInputDate());

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFetchingWorkload, setIsFetchingWorkload] = useState(false);

    // Accordion & Pagination State
    const [expandedVh, setExpandedVh] = useState(null);

    // Sidebar Ticket Details State
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [sidebarItems, setSidebarItems] = useState([]);
    const [sidebarIndex, setSidebarIndex] = useState(-1);

    const handleTicketClick = (ticketId, currentFilteredItems) => {
        if (!ticketId || Number(ticketId) <= 0 || !currentFilteredItems) return;
        
        // Filter out items without a valid ticket ID
        const validTickets = currentFilteredItems.filter(t => (t.ticket_id || t.id) && Number(t.ticket_id || t.id) > 0);
        const idx = validTickets.findIndex(t => (t.ticket_id || t.id) == ticketId);
        
        setSidebarItems(validTickets);
        setSidebarIndex(idx);
        setSelectedTicketId(ticketId);
    };
    const [vhDetails, setVhDetails] = useState({});
    const [vhLoading, setVhLoading] = useState({});
    const [vhSearch, setVhSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [popupData, setPopupData] = useState(null); // { type: 'timesheet' | 'ticket', memberName: string, items: Array }
    const [modalSearch, setModalSearch] = useState('');
    const [kpiPopupData, setKpiPopupData] = useState(null); // { type: 'absentees' | 'repeats' | 'retention', title: string, items: Array }
    const [kpiCurrentPage, setKpiCurrentPage] = useState(1);
    const [kpiSearch, setKpiSearch] = useState('');
    const [kpiSortOption, setKpiSortOption] = useState('default');
    const kpiItemsPerPage = 4;
    
    const [trendLoading, setTrendLoading] = useState(false);
    const [trendData, setTrendData] = useState([]);
    const monthOptions = [
        { value: '01', label: 'Jan' }, { value: '02', label: 'Feb' },
        { value: '03', label: 'Mar' }, { value: '04', label: 'Apr' },
        { value: '05', label: 'May' }, { value: '06', label: 'Jun' },
        { value: '07', label: 'Jul' }, { value: '08', label: 'Aug' },
        { value: '09', label: 'Sep' }, { value: '10', label: 'Oct' },
        { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' }
    ];

    const yearOptions = [
        { value: '2026', label: '2026' },
        { value: '2025', label: '2025' },
        { value: '2024', label: '2024' }
    ];

    const currentMonthNum = String(new Date().getMonth() + 1).padStart(2, '0');
    const currentYearNum = new Date().getFullYear().toString();
    const defaultMonth = monthOptions.find(m => m.value === currentMonthNum) || monthOptions[0];
    const defaultYear = yearOptions.find(y => y.value === currentYearNum) || yearOptions[0];

    const [trendSelectedMonths, setTrendSelectedMonths] = useState([]);
    const [trendSelectedYear, setTrendSelectedYear] = useState(defaultYear);

    useEffect(() => {
        if (!kpiPopupData) {
            // Clear trend data when popup closes so it doesn't flash stale data on next open
            setTrendData([]);
            return;
        }

        setKpiCurrentPage(1);
        setKpiSearch('');
        setKpiSortOption('default');

        if (kpiPopupData.type === 'efficiency_trend') {
            const fetchTrend = async () => {
                setTrendLoading(true);
                setTrendData([]); // Clear stale data before new fetch
                try {
                    const params = { vh_id: kpiPopupData.vhId };
                    if (trendSelectedMonths && trendSelectedMonths.length > 0) {
                        const year = trendSelectedYear ? trendSelectedYear.value : new Date().getFullYear().toString();
                        params.filterMonths = trendSelectedMonths.map(opt => `${year}-${opt.value}`).join(',');
                    }
                    const res = await api.get('charts/vertical-head-efficiency-trend', { params });
                    if (res.data?.status) {
                        setTrendData(res.data.data);
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    setTrendLoading(false);
                }
            };
            fetchTrend();
        }
    }, [kpiPopupData, trendSelectedMonths, trendSelectedYear]);

    const [sortField, setSortField] = useState('tickets');
    const [sortDirection, setSortDirection] = useState('desc');
    // Filter States
    const [taskTypeOptions, setTaskTypeOptions] = useState([]);
    const [customerOptionsFilter, setCustomerOptionsFilter] = useState([]);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);

    const [vhFilters, setVhFilters] = useState({}); // { [vhId]: { taskType, designations } }
    const [globalCustomer, setGlobalCustomer] = useState(null);
    const [globalEmployee, setGlobalEmployee] = useState([]);

    const updateVhFilter = (vhId, filterKey, value) => {
        setVhFilters(prev => ({
            ...prev,
            [vhId]: {
                ...(prev[vhId] || {}),
                [filterKey]: value
            }
        }));
    };

    useEffect(() => {
        const loadFormData = async () => {
            try {
                const r = await api.get('/timesheet/form-data');
                const empOpts = (r.data?.employees || []).map(e => ({ value: e.user_id, label: e.name }));
                const custOpts = (r.data?.customers || []).map(c => ({ value: c.cust_key, label: c.customer_name }));
                const desigOpts = [
                    { value: 'all', label: 'All' },
                    ...(r.data?.designations || []).map(d => ({ value: d.id, label: d.designation_name }))
                ];
                
                const seen = new Set();
                const uniqueCust = [];
                for (const o of custOpts) {
                    const k = (o.label || '').trim().toLowerCase();
                    if (!k || seen.has(k)) continue;
                    seen.add(k);
                    uniqueCust.push(o);
                }
                
                setEmployeeOptions(empOpts);
                setDesignationOptions(desigOpts);
                setCustomerOptionsFilter(uniqueCust);
    
                const types = [
                    { value: 'Ticket', label: 'Ticket' },
                    { value: 'DN', label: 'Design' },
                    { value: 'DT', label: 'Development' },
                    { value: 'QA', label: 'Testing' },
                    { value: 'UT', label: 'Unit Testing' },
                    { value: 'SS', label: 'Security Scan' },
                    { value: 'IM', label: 'Implementation' },
                    { value: 'TR', label: 'Training' },
                    { value: 'HR', label: 'HR' },
                    { value: 'OT', label: 'Others' },
                    { value: 'AD', label: 'Admin' },
                    { value: 'ST', label: 'System Testing' },
                    { value: 'BG', label: 'Bug Fixes in Staging' },
                    { value: 'RD', label: 'Requirement Discussion' },
                    { value: 'PT', label: 'Production Testing' },
                    { value: 'CT', label: 'Customer Testing' },
                    { value: 'PI', label: 'Post Implementation' },
                    { value: 'IA', label: 'Infrastructure Administration' },
                    { value: 'DP', label: 'DEMO PREP' }
                ];
                setTaskTypeOptions(types);
            } catch (e) {
                console.error('Failed to load form data', e);
            }
        };
        loadFormData();
    }, []);

    const handleSort = (e, field) => {
        e.stopPropagation();
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
        setCurrentPage(1);
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) {
            return <span className="text-slate-300 ml-1.5 text-[9px] select-none transition-colors group-hover:text-slate-400">↕</span>;
        }
        return sortDirection === 'asc' 
            ? <span className="text-red-600 font-bold ml-1.5 text-[9px] select-none">▲</span> 
            : <span className="text-red-600 font-bold ml-1.5 text-[9px] select-none">▼</span>;
    };

    const fetchWorkloadData = useCallback(async () => {
        try {
            setIsFetchingWorkload(true);
            setVhDetails({});
            const params = {
                fromDate,
                toDate
            };
            if (globalCustomer) params.customerId = globalCustomer.value;
            if (globalEmployee && globalEmployee.length > 0) params.employeeIds = globalEmployee.map(e => e.value).join(',');

            const res = await api.get('charts/vertical-heads-workload', {
                params
            });
            if (res.data && res.data.status) {
                setData(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching workload overview:", error);
            toast.error("Failed to load workload overview");
        } finally {
            setLoading(false);
            setIsFetchingWorkload(false);
        }
    }, [fromDate, toDate, globalCustomer, globalEmployee]);

    const handleOpenPopup = (type, member) => {
        let items = [];
        if (type === 'timesheet') {
            items = member.tasks || [];
        } else if (type === 'ticket') {
            items = member.tickets || [];
        }
        setModalSearch('');
        setPopupData({
            type,
            memberName: member.name,
            designation: member.designation,
            items
        });
    };

    const refetchVhDetails = useCallback(async (vhId) => {
        try {
            setVhLoading(prev => ({ ...prev, [vhId]: true }));
            const params = {
                vh_id: vhId,
                fromDate,
                toDate
            };
            
            const filters = vhFilters[vhId] || {};
            if (filters.taskType) params.taskType = filters.taskType.value;
            
            if (globalCustomer) params.customerId = globalCustomer.value;
            if (filters.customer) params.customerId = filters.customer.value;

            if (globalEmployee && globalEmployee.length > 0) params.employeeIds = globalEmployee.map(e => e.value).join(',');
            if (filters.employees && filters.employees.length > 0) params.employeeIds = filters.employees.map(e => e.value).join(',');

            if (filters.designations && filters.designations.length > 0) params.designationIds = filters.designations.map(d => d.value).join(',');

            const res = await api.get('charts/vertical-head-workload-details', {
                params
            });
            if (res.data && res.data.status) {
                setVhDetails(prev => ({ ...prev, [vhId]: res.data.data }));
            }
        } catch (error) {
            console.error("Error fetching workload details:", error);
            toast.error("Failed to load team details");
        } finally {
            setVhLoading(prev => ({ ...prev, [vhId]: false }));
        }
    }, [fromDate, toDate, vhFilters, globalCustomer, globalEmployee]);

    useEffect(() => {
        fetchWorkloadData();
    }, [fetchWorkloadData]);

    useEffect(() => {
        if (expandedVh) {
            refetchVhDetails(expandedVh);
        }
    }, [expandedVh, refetchVhDetails]);

    const handleCardClick = (vhId) => {
        if (expandedVh === vhId) {
            setExpandedVh(null);
            return;
        }
        setExpandedVh(vhId);
        setVhSearch('');
        setCurrentPage(1);
        setItemsPerPage(5); // Reset back to default of 5 entries when changing heads
        setSortField('tickets');
        setSortDirection('desc');
        
        refetchVhDetails(vhId);
    };

    const fetchRevenueReport = async (vhId, vhName) => {
        try {
            const toastId = toast.loading('Fetching revenue report...');
            const res = await api.get('charts/vertical-head-revenue-report', {
                params: { vh_id: vhId, fromDate, toDate }
            });
            toast.dismiss(toastId);
            
            if (res.data && res.data.status === 'success') {
                exportRevenueReportToExcel(`${vhName} — Revenue Report`, res.data.data);
            } else {
                toast.error('Failed to load revenue data');
            }
        } catch (error) {
            console.error('Error fetching revenue report:', error);
            toast.dismiss();
            toast.error('Failed to load revenue data');
        }
    };

    const exportRevenueReportToExcel = async (title, items) => {
        try {
            const dataItems = items || [];

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Revenue Report');

            // Company Title
            const companyCell = worksheet.getCell('A1');
            companyCell.value = 'eNova Software and Hardware Solutions P Ltd.';
            companyCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFB91C1C' } };
            companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(1).height = 30;

            // Title
            worksheet.mergeCells('A2:D2');
            const titleCell = worksheet.getCell('A2');
            titleCell.value = title;
            titleCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF333333' } };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(2).height = 20;

            // Date Period
            worksheet.mergeCells('A3:D3');
            const dateCell = worksheet.getCell('A3');
            dateCell.value = `Period: ${formatDateDDMMYYYY(fromDate)} to ${formatDateDDMMYYYY(toDate)}`;
            dateCell.font = { name: 'Arial', size: 9, italic: true, color: { argb: 'FF666666' } };
            dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(3).height = 18;

            // Headers for detailed tables
            const tableHeaders = ['Customer', 'Invoice Date', 'Pending (₹)', 'Days Pending'];

            worksheet.columns = [
                { width: 35 },
                { width: 15 },
                { width: 15 },
                { width: 15 }
            ];

            const headerRow = worksheet.addRow(tableHeaders);
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
            headerRow.eachCell((cell) => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF475569' } };
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
            });

            // Data Rows
            if (dataItems.length > 0) {
                let totalPendingAmount = 0;

                dataItems.forEach(item => {
                    const pendingAmount = Number(item.pending_amount || 0);
                    totalPendingAmount += pendingAmount;

                    const rowData = [
                        item.customer_name || '-',
                        item.invoice_date ? formatDateDDMMYYYY(item.invoice_date) : '-',
                        pendingAmount,
                        item.pending_amount > 0 ? (item.pending_days > 0 ? `${item.pending_days} Days` : 'Today') : '-'
                    ];

                    const dataRow = worksheet.addRow(rowData);
                    dataRow.alignment = { vertical: 'middle' };
                    // Align numeric columns right
                    dataRow.getCell(3).alignment = { horizontal: 'right', vertical: 'middle' };
                    dataRow.getCell(4).alignment = { horizontal: 'center', vertical: 'middle' };

                    dataRow.getCell(3).numFmt = '#,##0.00';
                    
                    dataRow.eachCell((cell) => {
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                            left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                            bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                            right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
                        };
                    });
                });

                // Add Total Row
                const totalRow = worksheet.addRow(['Total Pending Revenue', '', totalPendingAmount, '']);
                worksheet.mergeCells(`A${totalRow.number}:B${totalRow.number}`);
                
                totalRow.getCell(1).font = { bold: true, color: { argb: 'FF1F2937' } };
                totalRow.getCell(1).alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
                
                totalRow.getCell(3).font = { bold: true, color: { argb: 'FFB91C1C' } };
                totalRow.getCell(3).alignment = { horizontal: 'right', vertical: 'middle' };
                totalRow.getCell(3).numFmt = '#,##0.00';
                
                totalRow.eachCell((cell, colNumber) => {
                    if (colNumber <= 4) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FF94A3B8' } },
                            bottom: { style: 'medium', color: { argb: 'FF94A3B8' } },
                            left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                            right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
                        };
                    }
                });
                totalRow.height = 25;
            } else {
                const emptyRow = worksheet.addRow(['No pending revenue for this period']);
                worksheet.mergeCells(`A${emptyRow.number}:D${emptyRow.number}`);
                emptyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
                emptyRow.height = 30;
                emptyRow.getCell(1).font = { italic: true, color: { argb: 'FF94A3B8' } };
            }

            worksheet.mergeCells('A1:D1');

            // Save
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${title.replace(/[^a-zA-Z0-9 ]/g, '')}_Revenue.xlsx`;
            link.click();
            
        } catch (error) {
            console.error('Export Revenue Excel Error:', error);
            toast.error("Failed to export data");
        }
    };

    const exportAllToExcel = async () => {
        try {
            if (!data || data.length === 0) {
                toast.error("No data to export");
                return;
            }
            toast.loading("Gathering data for export...", { id: 'export' });

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Workload Overview');

            // Branding
            worksheet.mergeCells('A1:F1');
            const companyCell = worksheet.getCell('A1');
            companyCell.value = 'eNova Software and Hardware Solutions P Ltd.';
            companyCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFB91C1C' } };
            companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(1).height = 30;

            // Title
            worksheet.mergeCells('A2:F2');
            const titleCell = worksheet.getCell('A2');
            titleCell.value = `Vertical Heads & Team Workload Report`;
            titleCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF333333' } };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(2).height = 20;

            // Date Period
            worksheet.mergeCells('A3:F3');
            const dateCell = worksheet.getCell('A3');
            dateCell.value = `Period: ${formatDateDDMMYYYY(fromDate)} to ${formatDateDDMMYYYY(toDate)}`;
            dateCell.font = { name: 'Arial', size: 9, italic: true, color: { argb: 'FF666666' } };
            dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(3).height = 18;

            // Headers for detailed tables
            const tableHeaders = ['Employee Name', 'Timesheet', 'Ticket', 'Hours Logged', 'Efficiency'];

            worksheet.columns = [
                { width: 35 },
                { width: 12 },
                { width: 12 },
                { width: 15 },
                { width: 15 },
                { width: 15 }
            ];

            // Add Overall Summary Table
            const summaryHeaders = ['Head', 'Team Members', 'Active Tickets', 'Present %', 'Repeated Tickets', 'Retention %', 'Efficiency'];
            const summaryHeaderRow = worksheet.addRow(summaryHeaders);
            summaryHeaderRow.height = 22;
            summaryHeaderRow.eachCell((cell, idx) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB91C1C' } };
                cell.alignment = { horizontal: idx >= 2 ? 'center' : 'left', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            data.forEach(vh => {
                const summaryRowData = [
                    vh.name,
                    vh.team_count,
                    `${vh.total_workload}h`,
                    vh.ticket_count || 0,
                    vh.timesheet_count || 0,
                    `${vh.present_percentage ? Number(vh.present_percentage).toFixed(1) : '0.0'}%`,
                    vh.repeated_tickets || 0,
                    `${vh.retention_percentage ? Number(vh.retention_percentage).toFixed(1) : '0.0'}%`,
                    `${vh.avg_completion}%`
                ];
                const sRow = worksheet.addRow(summaryRowData);
                sRow.height = 20;
                sRow.eachCell((cell, colIndex) => {
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    if (colIndex >= 2) cell.alignment = { vertical: 'middle', horizontal: 'center' };
                });
            });

            worksheet.addRow([]); // Spacing

            let hasAnyData = false;
            for (const vh of data) {
                let teamData = vhDetails[vh.id];
                if (!teamData) {
                    try {
                        const res = await api.get('charts/vertical-head-workload-details', {
                            params: { vh_id: vh.id, fromDate, toDate }
                        });
                        teamData = res.data?.data || [];
                    } catch (e) {
                        teamData = [];
                    }
                }

                if (teamData && teamData.length > 0) {
                    hasAnyData = true;

                    // Add spacing
                    worksheet.addRow([]);

                    // Add Vertical Head Section Header
                    const vhRow = worksheet.addRow([`Vertical Head: ${vh.name}`]);
                    worksheet.mergeCells(`A${vhRow.number}:E${vhRow.number}`);
                    vhRow.height = 25;
                    vhRow.getCell(1).font = { bold: true, size: 11, color: { argb: 'FFB91C1C' } };
                    vhRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
                    vhRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
                    vhRow.getCell(1).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

                    // Add Table Headers
                    const headerRow = worksheet.addRow(tableHeaders);
                    headerRow.height = 22;
                    headerRow.eachCell((cell, idx) => {
                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF666666' } };
                        cell.alignment = { horizontal: idx >= 2 ? 'center' : 'left', vertical: 'middle' };
                        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    });

                    // Sort by total hours
                    const filteredTeam = teamData.filter(m => (m.tasks || []).length > 0 || (m.tickets || []).length > 0);
                    const sorted = [...filteredTeam].sort((a, b) => (parseFloat(b.total_hours) || 0) - (parseFloat(a.total_hours) || 0));
                    
                    if (sorted.length > 0) {
                        sorted.forEach(m => {
                            const rowData = [
                                `${m.name} (${m.designation})`,
                                (m.tasks || []).length,
                                (m.tickets || []).length,
                                `${m.total_hours || 0}h`,
                                `${m.avg_completion || 0}%`
                            ];
                            const dataRow = worksheet.addRow(rowData);
                            dataRow.height = 25;
                            dataRow.getCell(1).alignment = { wrapText: true, vertical: 'middle' };
                            dataRow.eachCell((cell, colIndex) => {
                                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                                if (colIndex !== 1) cell.alignment = { vertical: 'middle', horizontal: 'center' };
                            });
                        });
                    } else {
                        const dataRow = worksheet.addRow(['No active workloads in this period']);
                        worksheet.mergeCells(`A${dataRow.number}:E${dataRow.number}`);
                        dataRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                }
            }

            if (!hasAnyData) {
                toast.error("No data found for the selected period", { id: 'export' });
                return;
            }

            // ADD DETAILED LOGS SHEET
            try {
                const params = { fromDate, toDate };
                if (globalCustomer) params.customerId = globalCustomer.value;
                if (globalEmployee && globalEmployee.length > 0) params.employeeIds = globalEmployee.map(e => e.value).join(',');

                const logsRes = await api.get('charts/vertical-heads-export-details', { params });
                
                if (logsRes.data?.status === 'success' && logsRes.data.data?.length > 0) {
                    // Group the data
                    const groupedData = {};
                    logsRes.data.data.forEach(log => {
                        if (!groupedData[log.vertical_head]) groupedData[log.vertical_head] = {};
                        if (!groupedData[log.vertical_head][log.employee_name]) groupedData[log.vertical_head][log.employee_name] = [];
                        groupedData[log.vertical_head][log.employee_name].push(log);
                    });

                    const logHeaders = ['Date', 'Task / Ticket Name', 'Type', 'Description', 'Hours Logged', 'Efficiency'];

                    Object.keys(groupedData).forEach(vhName => {
                        // Create a safe sheet name (max 31 characters, strip invalid chars)
                        let safeSheetName = vhName.replace(/[:\\/?*[\]]/g, '').trim().substring(0, 31);
                        if (!safeSheetName) safeSheetName = 'Logs';
                        const wsLogs = workbook.addWorksheet(safeSheetName);
                        
                        wsLogs.columns = [
                            { width: 12 }, // Date
                            { width: 30 }, // Task
                            { width: 15 }, // Type
                            { width: 60 }, // Description
                            { width: 15 }, // Hours
                            { width: 15 }  // Efficiency
                        ];

                        // Vertical Head Header Row
                        const vhRow = wsLogs.addRow([`Vertical Head: ${vhName}`]);
                        wsLogs.mergeCells(`A${vhRow.number}:F${vhRow.number}`);
                        vhRow.height = 25;
                        vhRow.getCell(1).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
                        vhRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB91C1C' } }; 
                        vhRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
                        
                        Object.keys(groupedData[vhName]).forEach(empName => {
                            // Employee Header Row
                            const empRow = wsLogs.addRow([`Employee: ${empName}`]);
                            wsLogs.mergeCells(`A${empRow.number}:F${empRow.number}`);
                            empRow.height = 20;
                            empRow.getCell(1).font = { bold: true, size: 11, color: { argb: 'FF1F2937' } };
                            empRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }; 
                            empRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', indent: 2 };
                            
                            // Column Headers for this block
                            const headerRow = wsLogs.addRow(logHeaders);
                            headerRow.height = 18;
                            headerRow.eachCell((cell) => {
                                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B5563' } }; 
                                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                            });
                            
                            groupedData[vhName][empName].forEach(log => {
                                const dataRow = wsLogs.addRow([
                                    formatDateDDMMYYYY(log.timesheet_date),
                                    log.task_name,
                                    log.type,
                                    log.timesheet_description || '-',
                                    `${log.hours_logged}h`,
                                    `${log.efficiency}%`
                                ]);
                                dataRow.getCell(4).alignment = { wrapText: true, vertical: 'middle' };
                                dataRow.eachCell((cell, colIndex) => {
                                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                                    if (colIndex !== 2 && colIndex !== 4) cell.alignment = { vertical: 'middle', horizontal: 'center' };
                                });
                            });
                            
                            // Spacing
                            wsLogs.addRow([]);
                        });
                    });
                }
            } catch (err) {
                console.error("Error fetching detailed logs for export:", err);
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Workload_All_Vertical_Heads_${fromDate}_to_${toDate}.xlsx`);
            toast.success('Excel Report Exported successfully', { id: 'export' });
        } catch (error) {
            console.error("Excel export error:", error);
            toast.error("Failed to export Excel report", { id: 'export' });
        }
    };

    const exportAllToPDF = async () => {
        try {
            if (!data || data.length === 0) {
                toast.error("No data to export");
                return;
            }
            toast.loading("Gathering data for PDF export...", { id: 'export' });

            const doc = new jsPDF('p', 'pt', 'a4');
            
            doc.setFontSize(18);
            doc.setTextColor(185, 28, 28);
            doc.setFont("helvetica", "bold");
            doc.text("eNova Software and Hardware Solutions P Ltd.", 40, 50);

            doc.setFontSize(12);
            doc.setTextColor(30, 41, 59);
            doc.text(`Vertical Heads & Team Workload Report`, 40, 75);

            doc.setFontSize(10);
            doc.setTextColor(100, 116, 139);
            doc.setFont("helvetica", "oblique");
            doc.text(`Period: ${formatDateDDMMYYYY(fromDate)} to ${formatDateDDMMYYYY(toDate)}`, 40, 95);

            // Add Overall Summary Table
            let summaryBody = [];
            data.forEach(vh => {
                summaryBody.push([
                    vh.name || 'N/A',
                    vh.team_count || 0,
                    vh.ticket_count || 0,
                    `${vh.present_percentage ? Number(vh.present_percentage).toFixed(1) : '0.0'}%`,
                    vh.repeated_tickets_count || 0,
                    `${vh.retention_percentage ? Number(vh.retention_percentage).toFixed(1) : '0.0'}%`,
                    `${vh.avg_completion}%`
                ]);
            });

            autoTable(doc, {
                head: [['Head', 'Team Members', 'Active Tickets', 'Present %', 'Repeated Tickets', 'Retention %', 'Efficiency']],
                body: summaryBody,
                startY: 115,
                theme: 'grid',
                headStyles: {
                    fillColor: [185, 28, 28], // Red-600 (eNova brand red)
                    textColor: [255, 255, 255],
                    fontSize: 9,
                    fontStyle: 'bold',
                    halign: 'center',
                    cellPadding: 4
                },
                bodyStyles: {
                    fontSize: 8,
                    cellPadding: 4,
                    textColor: [51, 65, 85],
                    valign: 'middle'
                },
                columnStyles: {
                    0: { halign: 'left' },
                    1: { halign: 'center' },
                    2: { halign: 'center' },
                    3: { halign: 'center' },
                    4: { halign: 'center' },
                    5: { halign: 'center' },
                    6: { halign: 'center' },
                    7: { halign: 'center' },
                    8: { halign: 'center' }
                },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                margin: { left: 40, right: 40 }
            });

            let currentY = doc.lastAutoTable.finalY + 30;
            let hasAnyData = false;

            for (const vh of data) {
                let teamData = vhDetails[vh.id];
                if (!teamData) {
                    try {
                        const res = await api.get('charts/vertical-head-workload-details', {
                            params: { vh_id: vh.id, fromDate, toDate }
                        });
                        teamData = res.data?.data || [];
                    } catch(e) {
                        teamData = [];
                    }
                }
                
                if (teamData && teamData.length > 0) {
                    hasAnyData = true;
                    
                    if (currentY > doc.internal.pageSize.getHeight() - 100) {
                        doc.addPage();
                        currentY = 40;
                    }

                    doc.setFontSize(12);
                    doc.setTextColor(185, 28, 28);
                    doc.setFont("helvetica", "bold");
                    const headingText = vh.name === 'eNova Admin' ? 'eNova Admin' : `Vertical Head: ${vh.name}`;
                    doc.text(headingText, 40, currentY);
                    currentY += 12;

                    const filteredTeam = teamData.filter(m => (m.tasks || []).length > 0 || (m.tickets || []).length > 0);
                    const sorted = [...filteredTeam].sort((a, b) => (parseFloat(b.total_hours) || 0) - (parseFloat(a.total_hours) || 0));
                    let body = [];
                    
                    if (sorted.length > 0) {
                        sorted.forEach(m => {
                            body.push([
                                `${m.name}\n(${m.designation})`,
                                (m.tasks || []).length,
                                (m.tickets || []).length,
                                `${m.total_hours || 0}h`,
                                `${m.avg_completion || 0}%`
                            ]);
                        });
                    } else {
                        body.push(['No active workloads in this period', '', '', '', '']);
                    }

                    autoTable(doc, {
                        head: [['Employee Name', 'Timesheet', 'Ticket', 'Hours Logged', 'Efficiency']],
                        body: body,
                        startY: currentY,
                        theme: 'grid',
                        headStyles: {
                            fillColor: [102, 102, 102],
                            textColor: [255, 255, 255],
                            fontSize: 9,
                            fontStyle: 'bold',
                            halign: 'center',
                            cellPadding: 6
                        },
                        bodyStyles: {
                            fontSize: 8,
                            cellPadding: 6,
                            textColor: [51, 65, 85],
                            valign: 'middle'
                        },
                        columnStyles: {
                            0: { halign: 'left', cellWidth: 160 },
                            1: { halign: 'center', cellWidth: 80 },
                            2: { halign: 'center', cellWidth: 80 },
                            3: { halign: 'center', cellWidth: 100 },
                            4: { halign: 'center', cellWidth: 95 }
                        },
                        alternateRowStyles: { fillColor: [248, 250, 252] },
                        margin: { left: 40, right: 40 }
                    });

                    currentY = doc.lastAutoTable.finalY + 30;
                }
            }

            if (!hasAnyData) {
                toast.error("No data found for the selected period", { id: 'export' });
                return;
            }

            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(148, 163, 184);
                const dateStr = new Date().toLocaleString("en-GB");
                doc.text(`Page ${i} of ${totalPages} | Generated on ${dateStr}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 20, { align: 'right' });
            }

            const pdfBlob = doc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank');
            toast.success("PDF preview opened in new tab", { id: 'export' });
        } catch (error) {
            console.error("PDF export error:", error);
            toast.error("Failed to export PDF report", { id: 'export' });
        }
    };

    const exportSingleVhToExcel = async (vh) => {
        try {
            let teamData = vhDetails[vh.id];
            if (!teamData || teamData.length === 0) {
                toast.error("No team data found to export");
                return;
            }
            toast.loading(`Gathering data for ${vh.name}...`, { id: 'export-single' });

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Team Workload');

            // Branding & Title
            worksheet.mergeCells('A1:E1');
            const companyCell = worksheet.getCell('A1');
            companyCell.value = 'eNova Software and Hardware Solutions P Ltd.';
            companyCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFB91C1C' } };
            companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(1).height = 30;

            worksheet.mergeCells('A2:E2');
            const titleCell = worksheet.getCell('A2');
            titleCell.value = `Workload Report: ${vh.name} (${vh.designation})`;
            titleCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF333333' } };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(2).height = 20;

            worksheet.mergeCells('A3:E3');
            const dateCell = worksheet.getCell('A3');
            dateCell.value = `Period: ${formatDateDDMMYYYY(fromDate)} to ${formatDateDDMMYYYY(toDate)}`;
            dateCell.font = { name: 'Arial', size: 9, italic: true, color: { argb: 'FF666666' } };
            dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(3).height = 18;
            
            worksheet.addRow([]);

            const tableHeaders = ['Employee Name', 'Timesheet', 'Ticket', 'Hours Logged', 'Efficiency'];
            worksheet.columns = [
                { width: 35 }, { width: 12 }, { width: 12 }, { width: 15 }, { width: 15 }
            ];

            const headerRow = worksheet.addRow(tableHeaders);
            headerRow.height = 22;
            headerRow.eachCell((cell, idx) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB91C1C' } }; // using red for individual
                cell.alignment = { horizontal: idx >= 2 ? 'center' : 'left', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            const filteredTeam = teamData.filter(m => (m.tasks || []).length > 0 || (m.tickets || []).length > 0);
            const sorted = [...filteredTeam].sort((a, b) => (parseFloat(b.total_hours) || 0) - (parseFloat(a.total_hours) || 0));
            
            if (sorted.length > 0) {
                sorted.forEach(m => {
                    const rowData = [
                        `${m.name} (${m.designation})`,
                        (m.tasks || []).length,
                        (m.tickets || []).length,
                        `${m.total_hours || 0}h`,
                        `${m.avg_completion || 0}%`
                    ];
                    const dataRow = worksheet.addRow(rowData);
                    dataRow.height = 25;
                    dataRow.getCell(1).alignment = { wrapText: true, vertical: 'middle' };
                    dataRow.eachCell((cell, colIndex) => {
                        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                        if (colIndex !== 1) cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    });
                });
            } else {
                const dataRow = worksheet.addRow(['No active workloads in this period']);
                worksheet.mergeCells(`A${dataRow.number}:E${dataRow.number}`);
                dataRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Workload_${vh.name.replace(/\s+/g, '_')}_${fromDate}_to_${toDate}.xlsx`);
            toast.success('Excel Report Exported successfully', { id: 'export-single' });
        } catch (error) {
            console.error("Excel export error:", error);
            toast.error("Failed to export Excel report", { id: 'export-single' });
        }
    };

    const exportSingleVhToPDF = async (vh) => {
        try {
            let teamData = vhDetails[vh.id];
            if (!teamData || teamData.length === 0) {
                toast.error("No team data found to export");
                return;
            }
            toast.loading(`Gathering data for ${vh.name}...`, { id: 'export-single' });

            const doc = new jsPDF('p', 'pt', 'a4');
            
            doc.setFontSize(18);
            doc.setTextColor(185, 28, 28);
            doc.setFont("helvetica", "bold");
            doc.text("eNova Software and Hardware Solutions P Ltd.", 40, 50);

            doc.setFontSize(12);
            doc.setTextColor(30, 41, 59);
            doc.text(`Workload Report: ${vh.name} (${vh.designation})`, 40, 75);

            doc.setFontSize(10);
            doc.setTextColor(100, 116, 139);
            doc.setFont("helvetica", "oblique");
            doc.text(`Period: ${formatDateDDMMYYYY(fromDate)} to ${formatDateDDMMYYYY(toDate)}`, 40, 95);

            const filteredTeam = teamData.filter(m => (m.tasks || []).length > 0 || (m.tickets || []).length > 0);
            const sorted = [...filteredTeam].sort((a, b) => (parseFloat(b.total_hours) || 0) - (parseFloat(a.total_hours) || 0));
            let body = [];
            
            if (sorted.length > 0) {
                sorted.forEach(m => {
                    body.push([
                        `${m.name}\n(${m.designation})`,
                        (m.tasks || []).length,
                        (m.tickets || []).length,
                        `${m.total_hours || 0}h`,
                        `${m.avg_completion || 0}%`
                    ]);
                });
            } else {
                body.push(['No active workloads in this period', '', '', '', '']);
            }

            autoTable(doc, {
                head: [['Employee Name', 'Timesheet', 'Ticket', 'Hours Logged', 'Efficiency']],
                body: body,
                startY: 115,
                theme: 'grid',
                headStyles: {
                    fillColor: [185, 28, 28],
                    textColor: [255, 255, 255],
                    fontSize: 10,
                    fontStyle: 'bold',
                    halign: 'center',
                    cellPadding: 8
                },
                bodyStyles: {
                    fontSize: 9,
                    cellPadding: 8,
                    textColor: [51, 65, 85],
                    valign: 'middle'
                },
                columnStyles: {
                    0: { halign: 'left', cellWidth: 160 },
                    1: { halign: 'center', cellWidth: 80 },
                    2: { halign: 'center', cellWidth: 80 },
                    3: { halign: 'center', cellWidth: 100 },
                    4: { halign: 'center', cellWidth: 95 }
                },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                margin: { left: 40, right: 40 }
            });

            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(148, 163, 184);
                const dateStr = new Date().toLocaleString("en-GB");
                doc.text(`Page ${i} of ${totalPages} | Generated on ${dateStr}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 20, { align: 'right' });
            }

            const pdfBlob = doc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, '_blank');
            toast.success("PDF preview opened in new tab", { id: 'export-single' });
        } catch (error) {
            console.error("PDF export error:", error);
            toast.error("Failed to export PDF report", { id: 'export-single' });
        }
    };

    // Date Range Adjustments
    const handlePrevPeriod = () => {
        const start = new Date(fromDate + 'T00:00:00');
        if (isNaN(start.getTime())) return;
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);
        
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);

        setFromDate(toInputDate(start));
        setToDate(toInputDate(end));
    };

    const handleNextPeriod = () => {
        const start = new Date(fromDate + 'T00:00:00');
        if (isNaN(start.getTime())) return;
        start.setMonth(start.getMonth() + 1);
        start.setDate(1);
        
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);

        setFromDate(toInputDate(start));
        setToDate(toInputDate(end));
    };

    const handleReset = () => {
        setFromDate(getFirstDayOfMonthInputDate());
        setToDate(getTodayInputDate());
    };

    const exportAllToImage = async () => {
        const element = document.getElementById('workload-list-container');
        if (!element) return;
        
        toast.loading('Generating image...', { id: 'image-export' });
        try {
            const { toPng } = await import('html-to-image');
            const filter = (node) => {
                if (node.classList && node.classList.contains('no-export-image')) return false;
                return true;
            };
            const dataUrl = await toPng(element, {
                cacheBust: true,
                backgroundColor: '#ffffff',
                pixelRatio: 2,
                filter: filter
            });
            
            const link = document.createElement('a');
            link.download = `Workload_Dashboard_${formatDateDDMMYYYY(fromDate)}_to_${formatDateDDMMYYYY(toDate)}.png`;
            link.href = dataUrl;
            link.click();
            
            toast.success('Image Exported successfully', { id: 'image-export' });
        } catch (error) {
            console.error('Image export error:', error);
            toast.error('Failed to export image', { id: 'image-export' });
        }
    };

    return (
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden hover:shadow-2xl transition-all duration-500">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-50 flex flex-col lg:flex-row justify-between items-center bg-gradient-to-r from-white to-slate-50 gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
                        <FiActivity size={22} />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-slate-800 tracking-tight">Workload Tracker: Vertical Heads</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                            Team logged hours and average completion percentages
                        </p>
                    </div>
                </div>

                {/* Premium Date Range Picker - Matches Image 2 */}
                <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Global Filters */}
                    <div className="w-36 sm:w-44">
                        <Select
                            options={customerOptionsFilter}
                            value={globalCustomer}
                            onChange={setGlobalCustomer}
                            placeholder="All Customers"
                            isClearable
                            className="text-xs"
                            styles={{
                                control: (provided) => ({ ...provided, borderColor: '#e5e7eb', borderRadius: '0.75rem', minHeight: '36px', boxShadow: 'none', '&:hover': { borderColor: '#4f46e5' } }),
                                menu: (provided) => ({ ...provided, zIndex: 50 })
                            }}
                        />
                    </div>
                    <div className="w-36 sm:w-44">
                        <Select
                            isMulti
                            hideSelectedOptions={false}
                            closeMenuOnSelect={true}
                            components={{ MultiValue: CustomMultiValue }}
                            options={employeeOptions}
                            value={globalEmployee}
                            onChange={setGlobalEmployee}
                            placeholder="Employees..."
                            className="text-xs"
                            styles={{
                                control: (provided) => ({ ...provided, borderColor: '#e5e7eb', borderRadius: '0.75rem', minHeight: '36px', boxShadow: 'none', '&:hover': { borderColor: '#4f46e5' } }),
                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        />
                    </div>

                    <button
                        onClick={handlePrevPeriod}
                        className="p-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-500 hover:text-slate-700 shadow-sm transition-colors"
                        title="Previous Period"
                    >
                        <FiChevronLeft size={16} />
                    </button>

                    <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 shadow-sm hover:border-slate-300 transition-colors">
                        <DatePicker
                            selected={new Date(fromDate + 'T00:00:00')}
                            maxDate={new Date(toDate + 'T00:00:00')}
                            onChange={(date) => {
                                if (date) setFromDate(toInputDate(date));
                            }}
                            dateFormat="dd/MM/yyyy"
                            className="text-xs text-slate-700 font-bold bg-transparent border-0 border-none p-0 outline-none focus:outline-none focus:ring-0 cursor-pointer w-24 text-center"
                        />
                    </div>

                    <span className="text-xs font-bold text-slate-400 px-1">to</span>

                     <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 shadow-sm hover:border-slate-300 transition-colors">
                        <DatePicker
                            selected={new Date(toDate + 'T00:00:00')}
                            minDate={new Date(fromDate + 'T00:00:00')}
                            onChange={(date) => {
                                if (date) setToDate(toInputDate(date));
                            }}
                            dateFormat="dd/MM/yyyy"
                            className="text-xs text-slate-700 font-bold bg-transparent border-0 border-none p-0 outline-none focus:outline-none focus:ring-0 cursor-pointer w-24 text-center"
                        />
                    </div>

                    <button
                        onClick={handleNextPeriod}
                        className="p-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-500 hover:text-slate-700 shadow-sm transition-colors"
                        title="Next Period"
                    >
                        <FiChevronRight size={16} />
                    </button>

                    <button
                        onClick={handleReset}
                        className="p-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-500 hover:text-slate-700 shadow-sm transition-colors"
                        title="Reset Range"
                    >
                        <FiRotateCcw size={16} />
                    </button>

                    {/* Global Export Buttons */}
                    <div className="w-px h-6 bg-slate-200 mx-1 hidden sm:block"></div>
                    <button
                        onClick={exportAllToExcel}
                        className="p-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-800 transition active:scale-95 shadow-sm flex items-center justify-center cursor-pointer"
                        title="Export All to Excel"
                    >
                        <RiFileExcelFill className="text-lg" />
                    </button>
                    <button
                        onClick={exportAllToPDF}
                        className="p-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-red-600 transition active:scale-95 shadow-sm flex items-center justify-center cursor-pointer"
                        title="Export All to PDF"
                    >
                        <RiFilePdf2Fill className="text-lg" />
                    </button>
                    <button
                        onClick={exportAllToImage}
                        className="py-1.5 px-3 bg-[#da2a2a] hover:bg-[#b91c1c] rounded-lg text-white font-bold transition active:scale-95 shadow-sm flex items-center justify-center cursor-pointer gap-1.5 text-[13px] tracking-wide"
                        title="Export to Image"
                    >
                        <FiDownload size={14} strokeWidth={3} />
                        <span>Image</span>
                    </button>
                </div>
            </div>

            {/* Content / Accordion list of Vertical Heads */}
            <div className="p-6">
                {loading || isFetchingWorkload ? (
                    <div className="flex flex-col items-center py-20 gap-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-transparent border-t-red-500 border-r-red-500"></div>
                        <p className="text-[13px] font-medium text-slate-500">Loading...</p>
                    </div>
                ) : data.length === 0 ? (
                    <div className="text-center py-16 text-slate-400 font-medium text-sm">
                        No workload data found for the selected period.
                    </div>
                ) : (
                    <div>
                        {/* Interactive Hint */}
                        <div className="flex items-center justify-end gap-1.5 px-2 mb-2 -mt-2">
                            <FiInfo className="text-slate-400" size={13} />
                            <span className="text-[11px] font-medium text-slate-500">
                                Click on <span className="font-bold text-slate-700">Efficiency</span>, <span className="font-bold text-slate-700">Present %</span>, <span className="font-bold text-slate-700">Repeated Tickets</span>, or <span className="font-bold text-slate-700">Retention %</span> metrics to view detailed team breakdown.
                            </span>
                        </div>
                        <div id="workload-list-container" className="flex flex-col gap-4">
                        {data.map((vh) => {
                            const completion = vh.avg_completion;
                            const isVhExpanded = expandedVh === vh.id;
                            const currentFilters = vhFilters[vh.id] || {};
                            
                            // Determine Progress Bar Colors
                            let barColor = "from-rose-500 to-rose-400";
                            let textColor = "text-rose-600";
                            if (completion >= 75) {
                                barColor = "from-emerald-500 to-emerald-400";
                                textColor = "text-emerald-600";
                            } else if (completion >= 40) {
                                barColor = "from-amber-500 to-amber-400";
                                textColor = "text-amber-600";
                            }

                            return (
                                <div
                                    key={vh.id}
                                    className="bg-slate-50/50 rounded-2xl border border-slate-100/80 overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md"
                                >
                                    {/* Main Row summary card */}
                                    <div
                                        onClick={() => handleCardClick(vh.id)}
                                        className="cursor-pointer bg-slate-50/30 hover:bg-white p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 group transition-all duration-300 border-b border-transparent hover:border-slate-100 hover:shadow-sm"
                                        title={isVhExpanded ? "Click to collapse team details" : "Click to view team details"}
                                    >
                                        {/* Left Column: Chevron + Photo + Name & Designation */}
                                        <div className="flex items-center gap-3 w-full md:w-[28%] min-w-0">
                                            <FiChevronDown
                                                size={16}
                                                className={`text-red-500 transition-transform duration-300 shrink-0 ${
                                                    isVhExpanded ? 'rotate-180' : ''
                                                }`}
                                            />
                                            {vh.image ? (
                                                <img src={vh.image} alt={vh.name} className="w-9 h-9 rounded-xl object-cover shrink-0 border border-slate-100 shadow-sm" />
                                            ) : (
                                                <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600 font-bold flex items-center justify-center text-xs shrink-0 shadow-sm">
                                                    {vh.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <h4 className="font-bold text-slate-800 text-sm sm:text-base tracking-tight truncate group-hover:text-indigo-600 transition-colors">
                                                    {vh.name}
                                                </h4>
                                                <p className="text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-tight truncate mt-0.5">
                                                    {vh.designation}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Right Columns: Stats & Progress Bar */}
                                        <div className="flex-1 w-full md:w-auto max-md:overflow-x-auto max-md:custom-scrollbar pb-1 md:pb-0">
                                            <div className="flex items-center justify-between gap-3 sm:gap-4 min-w-[620px] md:min-w-0 md:w-full">
                                                {/* TEAM Column */}
                                                <div className="w-[85px] md:w-auto md:flex-1 shrink-0">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">TEAM MEMBERS</span>
                                                    <span className="text-sm sm:text-base font-black text-slate-800 mt-0.5 block">{vh.team_count}</span>
                                                </div>

                                                {/* TICKETS Column */}
                                                <div className="w-[85px] md:w-auto md:flex-1 shrink-0">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">TICKETS</span>
                                                    <span className="text-sm sm:text-base font-black text-slate-800 mt-0.5 block">{vh.ticket_count || 0}</span>
                                                </div>

                                                {/* PRESENT Column */}
                                                <div 
                                                    className="w-[95px] md:w-auto md:flex-1 shrink-0 cursor-pointer group hover:bg-slate-100 p-1.5 -m-1.5 rounded transition-all"
                                                    onClick={(e) => { e.stopPropagation(); setKpiPopupData({ type: 'absentees', title: `${vh.name} — Team Absentees`, items: vh.absentees_list || [] }); }}
                                                    title="Click to view absentees list"
                                                >
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block group-hover:text-indigo-500 transition-colors">PRESENT %</span>
                                                    <span className={`text-sm sm:text-base font-black mt-0.5 inline-block border-b border-dashed border-slate-300 group-hover:border-indigo-400 ${vh.present_percentage >= 80 ? 'text-emerald-600' : vh.present_percentage >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                                                        {vh.present_percentage ? Number(vh.present_percentage).toFixed(1) : '0.0'}%
                                                    </span>
                                                </div>

                                                {/* REPEATED Column */}
                                                <div 
                                                    className="w-[115px] md:w-auto md:flex-1 shrink-0 cursor-pointer group hover:bg-slate-100 p-1.5 -m-1.5 rounded transition-all"
                                                    onClick={(e) => { e.stopPropagation(); setKpiPopupData({ type: 'repeats', title: `${vh.name} — Repeated Tickets`, items: vh.repeated_tickets_list || [] }); }}
                                                    title="Click to view repeated tickets details"
                                                >
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block group-hover:text-indigo-500 transition-colors">REPEATED TICKETS</span>
                                                    <span className="text-sm sm:text-base font-black text-slate-800 mt-0.5 inline-block border-b border-dashed border-slate-300 group-hover:border-indigo-400">{vh.repeated_tickets || 0}</span>
                                                </div>

                                                {/* RETENTION Column */}
                                                <div 
                                                    className="w-[95px] md:w-auto md:flex-1 shrink-0 cursor-pointer group hover:bg-slate-100 p-1.5 -m-1.5 rounded transition-all"
                                                    onClick={(e) => { e.stopPropagation(); setKpiPopupData({ type: 'retention', title: `${vh.name} — Terminated Members`, items: vh.terminated_list || [] }); }}
                                                    title="Click to view terminated members"
                                                >
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block group-hover:text-indigo-500 transition-colors">RETENTION %</span>
                                                    <span className={`text-sm sm:text-base font-black mt-0.5 inline-block border-b border-dashed border-slate-300 group-hover:border-indigo-400 ${vh.retention_percentage >= 90 ? 'text-emerald-600' : vh.retention_percentage >= 70 ? 'text-amber-600' : 'text-rose-600'}`}>
                                                        {vh.retention_percentage ? Number(vh.retention_percentage).toFixed(1) : '0.0'}%
                                                    </span>
                                                </div>

                                                {/* Progress Column */}
                                                <div className="w-[140px] md:w-auto md:flex-[1.5] shrink-0 flex items-end gap-2">
                                                    <div 
                                                        className="flex-1 pb-1 cursor-pointer group hover:bg-slate-100 p-1.5 -m-1.5 rounded transition-all"
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            setTrendSelectedMonths([]); 
                                                            setTrendSelectedYear(defaultYear);
                                                            setKpiPopupData({ type: 'efficiency_trend', title: `${vh.name} — Efficiency Trend`, vhId: vh.id, vhName: vh.name }); 
                                                        }}
                                                        title="Click to view efficiency trend"
                                                    >
                                                        <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 mb-1.5 group-hover:text-indigo-500 transition-colors">
                                                            <span>Efficiency</span>
                                                            <span className="font-bold text-slate-800 border-b border-dashed border-slate-300 group-hover:border-indigo-400">{completion}%</span>
                                                        </div>
                                                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                            <div
                                                                style={{ width: `${completion}%`, transition: 'width 0.4s ease-out' }}
                                                                className="h-full bg-red-600 rounded-full"
                                                            />
                                                        </div>
                                                    </div>
                                                    {vh.has_revenue && (
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                fetchRevenueReport(vh.id, vh.name);
                                                            }}
                                                            className="group flex items-center gap-1.5 px-2.5 py-1 bg-[#0b8258] hover:bg-[#086343] text-white rounded transition-all active:scale-95 shadow-sm cursor-pointer mb-0.5 no-export-image"
                                                            title="Download Revenue Report"
                                                        >
                                                            <FiDownload size={11} className="transition-transform group-hover:-translate-y-0.5" />
                                                            <span className="text-[10px] font-bold tracking-wide capitalize">Revenue Report</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Inline Accordion Detail Area */}
                                    <AnimatePresence>
                                        {isVhExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                className="overflow-hidden bg-slate-50/30 border-t border-slate-100"
                                            >
                                                {/* Search & Select dropdown inside Accordion */}
                                                <div className="px-6 py-3 bg-white border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-3">
                                                    {/* Left side: Period */}
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest self-start sm:self-center">
                                                        Period: {formatDateDDMMYYYY(fromDate)} to {formatDateDDMMYYYY(toDate)}
                                                    </div>

                                                    {/* Right side: Search & Limit dropdown */}
                                                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto justify-end">


                                                            {/* Limit dropdown selection */}
                                                            <div className="flex items-center gap-2 self-start sm:self-auto" onClick={(e) => e.stopPropagation()}>
                                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">SHOW</span>
                                                                <div className="relative group">
                                                                    <select
                                                                        value={itemsPerPage}
                                                                        onChange={(e) => {
                                                                            setItemsPerPage(Number(e.target.value));
                                                                            setCurrentPage(1);
                                                                        }}
                                                                    className="appearance-none pl-4 pr-8 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-2xl hover:border-slate-350 transition-all cursor-pointer shadow-sm outline-none"
                                                                >
                                                                    <option value={5}>5</option>
                                                                    <option value={10}>10</option>
                                                                    <option value={25}>25</option>
                                                                    <option value={50}>50</option>
                                                                </select>
                                                                <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600 transition-colors" size={12} />
                                                            </div>
                                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">ENTRIES</span>
                                                        </div>

                                                        {/* Individual Export Buttons */}
                                                        <div className="w-px h-5 bg-slate-200 hidden sm:block"></div>
                                                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                            <button onClick={() => exportSingleVhToExcel(vh)} className="p-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-slate-800 transition active:scale-95 shadow-sm flex items-center justify-center cursor-pointer" title="Export Team to Excel">
                                                                <RiFileExcelFill size={15} />
                                                            </button>
                                                            <button onClick={() => exportSingleVhToPDF(vh)} className="p-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-red-600 transition active:scale-95 shadow-sm flex items-center justify-center cursor-pointer" title="Export Team to PDF">
                                                                <RiFilePdf2Fill size={15} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Filter Section for this Vertical Head */}
                                                <div className="px-6 py-4 bg-white/50 border-b border-slate-100" onClick={(e) => e.stopPropagation()}>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                                        <div>
                                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Task Type</label>
                                                            <Select
                                                                options={taskTypeOptions}
                                                                value={currentFilters.taskType || null}
                                                                onChange={(val) => updateVhFilter(vh.id, 'taskType', val)}
                                                                placeholder="All Types"
                                                                isClearable
                                                                className="text-xs"
                                                                styles={{
                                                                    control: (provided) => ({ ...provided, borderColor: '#e5e7eb', borderRadius: '0.5rem', minHeight: '36px', boxShadow: 'none', '&:hover': { borderColor: '#4f46e5' } }),
                                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                                }}
                                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Customer</label>
                                                            <Select
                                                                options={customerOptionsFilter}
                                                                value={currentFilters.customer || null}
                                                                onChange={(val) => updateVhFilter(vh.id, 'customer', val)}
                                                                placeholder={globalCustomer ? `Override: ${globalCustomer.label}` : "All Customers"}
                                                                isClearable
                                                                className="text-xs"
                                                                styles={{
                                                                    control: (provided) => ({ ...provided, borderColor: '#e5e7eb', borderRadius: '0.5rem', minHeight: '36px', boxShadow: 'none', '&:hover': { borderColor: '#4f46e5' } }),
                                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                                }}
                                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Employees</label>
                                                            <Select
                                                                isMulti
                                                                hideSelectedOptions={false}
                                                                closeMenuOnSelect={true}
                                                                components={{ MultiValue: CustomMultiValue }}
                                                                options={employeeOptions}
                                                                value={currentFilters.employees || []}
                                                                onChange={(val) => updateVhFilter(vh.id, 'employees', val)}
                                                                placeholder={globalEmployee && globalEmployee.length > 0 ? "Override selection..." : "Select employees..."}
                                                                className="text-xs"
                                                                styles={{
                                                                    control: (provided) => ({ ...provided, borderColor: '#e5e7eb', borderRadius: '0.5rem', minHeight: '36px', boxShadow: 'none', '&:hover': { borderColor: '#4f46e5' } }),
                                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                                }}
                                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Roles</label>
                                                            <Select
                                                                isMulti
                                                                hideSelectedOptions={false}
                                                                closeMenuOnSelect={true}
                                                                components={{ MultiValue: CustomMultiValue }}
                                                                options={designationOptions}
                                                                value={currentFilters.designations || []}
                                                                onChange={(val) => updateVhFilter(vh.id, 'designations', val)}
                                                                placeholder="Select roles..."
                                                                className="text-xs"
                                                                styles={{
                                                                    control: (provided) => ({ ...provided, borderColor: '#e5e7eb', borderRadius: '0.5rem', minHeight: '36px', boxShadow: 'none', '&:hover': { borderColor: '#4f46e5' } }),
                                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                                }}
                                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Team List Area - Single unified container with line dividers */}
                                                <div className={`px-6 py-4 ${
                                                    itemsPerPage > 5 ? 'max-h-[400px] overflow-y-auto custom-scrollbar' : ''
                                                }`}>
                                                    {vhLoading[vh.id] ? (
                                                        <div className="flex flex-col justify-center items-center py-10 gap-3">
                                                            <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-transparent border-t-red-500 border-r-red-500"></div>
                                                            <span className="text-[12px] font-medium text-slate-500">Loading...</span>
                                                        </div>
                                                    ) : !vhDetails[vh.id] || vhDetails[vh.id].length === 0 ? (
                                                        <div className="text-center py-8 text-slate-400 text-xs font-medium">
                                                            No team members found for this selection.
                                                        </div>
                                                    ) : (() => {
                                                        const filteredTeam = (vhDetails[vh.id] || []).filter(member => 
                                                            member.name.toLowerCase().includes(vhSearch.toLowerCase()) ||
                                                            member.designation.toLowerCase().includes(vhSearch.toLowerCase())
                                                        );

                                                        const sortedTeam = [...filteredTeam].sort((a, b) => {
                                                            let aVal, bVal;
                                                            if (sortField === 'name') {
                                                                aVal = a.name.toLowerCase();
                                                                bVal = b.name.toLowerCase();
                                                            } else if (sortField === 'timesheet') {
                                                                aVal = a.tasks.length;
                                                                bVal = b.tasks.length;
                                                            } else if (sortField === 'ticket') {
                                                                aVal = (a.tickets || []).length;
                                                                bVal = (b.tickets || []).length;
                                                            } else if (sortField === 'total_hours') {
                                                                aVal = parseFloat(a.total_hours) || 0;
                                                                bVal = parseFloat(b.total_hours) || 0;
                                                            } else if (sortField === 'avg_completion') {
                                                                aVal = parseFloat(a.avg_completion) || 0;
                                                                bVal = parseFloat(b.avg_completion) || 0;
                                                            }

                                                            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                                                            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                                                            return 0;
                                                        });
                                                        const totalPages = Math.ceil(sortedTeam.length / itemsPerPage);
                                                        const paginatedTeam = sortedTeam.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

                                                        return (
                                                            <>
                                                                <div className="border border-slate-100 rounded-2xl overflow-x-auto custom-scrollbar bg-white shadow-sm">
                                                                    <table className="w-full min-w-[600px] text-left border-collapse">
                                                                        <thead>
                                                                            <tr className="border-b border-slate-100 text-[10px] uppercase text-slate-400 font-black tracking-wider bg-slate-50/30">
                                                                                <th onClick={(e) => handleSort(e, 'name')} className="px-4 py-3 font-black cursor-pointer hover:bg-slate-100/50 hover:text-slate-700 transition duration-200 group select-none">
                                                                                    <div className="flex items-center">
                                                                                        Employee Name
                                                                                        {renderSortIcon('name')}
                                                                                    </div>
                                                                                </th>
                                                                                <th onClick={(e) => handleSort(e, 'timesheet')} className="px-4 py-3 font-black cursor-pointer hover:bg-slate-100/50 hover:text-slate-700 transition duration-200 group select-none">
                                                                                    <div className="flex items-center">
                                                                                        Timesheet
                                                                                        {renderSortIcon('timesheet')}
                                                                                    </div>
                                                                                </th>
                                                                                <th onClick={(e) => handleSort(e, 'ticket')} className="px-4 py-3 font-black cursor-pointer hover:bg-slate-100/50 hover:text-slate-700 transition duration-200 group select-none">
                                                                                    <div className="flex items-center">
                                                                                        Ticket
                                                                                        {renderSortIcon('ticket')}
                                                                                    </div>
                                                                                </th>
                                                                                <th onClick={(e) => handleSort(e, 'present_percentage')} className="px-4 py-3 font-black cursor-pointer hover:bg-slate-100/50 hover:text-slate-700 transition duration-200 group select-none text-center">
                                                                                    <div className="flex items-center justify-center whitespace-nowrap">
                                                                                        Present %
                                                                                        {renderSortIcon('present_percentage')}
                                                                                    </div>
                                                                                </th>

                                                                                <th onClick={(e) => handleSort(e, 'avg_completion')} className="px-4 py-3 font-black cursor-pointer hover:bg-slate-100/50 hover:text-slate-700 transition duration-200 group select-none text-center">
                                                                                    <div className="flex items-center justify-center">
                                                                                        Efficiency
                                                                                        {renderSortIcon('avg_completion')}
                                                                                    </div>
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-slate-100 text-xs text-slate-600">
                                                                            {paginatedTeam.map(member => {
                                                                                const ticketCount = (member.tickets || []).length;

                                                                                return (
                                                                                    <tr
                                                                                        key={member.user_id}
                                                                                        className="hover:bg-slate-50/50 transition-colors border-b border-slate-100"
                                                                                    >
                                                                                        <td className="px-4 py-2.5">
                                                                                            <div className="flex items-center gap-3">
                                                                                                {member.image ? (
                                                                                                    <img src={member.image} alt={member.name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                                                                                                ) : (
                                                                                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-600 font-bold flex items-center justify-center text-[10px] shrink-0">
                                                                                                        {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                                                                    </div>
                                                                                                )}
                                                                                                <div>
                                                                                                    <h5 className="text-xs font-black text-slate-800">{member.name}</h5>
                                                                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">{member.designation}</p>
                                                                                                </div>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="px-4 py-2.5">
                                                                                            <button
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleOpenPopup('timesheet', member);
                                                                                                }}
                                                                                                className="text-red-600 hover:underline font-extrabold text-[13px] cursor-pointer focus:outline-none bg-transparent border-0 p-0"
                                                                                                title="View Timesheet Logs"
                                                                                            >
                                                                                                {member.tasks.length}
                                                                                            </button>
                                                                                        </td>
                                                                                        <td className="px-4 py-2.5">
                                                                                            <button
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleOpenPopup('ticket', member);
                                                                                                }}
                                                                                                className={`font-extrabold text-[13px] focus:outline-none bg-transparent border-0 p-0 ${
                                                                                                    ticketCount > 0
                                                                                                        ? 'text-red-600 hover:underline cursor-pointer'
                                                                                                        : 'text-slate-400 cursor-default'
                                                                                                }`}
                                                                                                disabled={ticketCount === 0}
                                                                                                title="View Tickets Worked"
                                                                                            >
                                                                                                {ticketCount}
                                                                                            </button>
                                                                                        </td>
                                                                                        <td className="px-4 py-2.5 text-center">
                                                                                            <span className={`inline-block font-black px-2 py-0.5 rounded text-[10px] ${
                                                                                                member.present_percentage >= 80 ? 'text-emerald-700 bg-emerald-50' :
                                                                                                member.present_percentage >= 50 ? 'text-amber-700 bg-amber-50' : 'text-rose-700 bg-rose-50'
                                                                                            }`}>
                                                                                                {member.present_percentage ? Number(member.present_percentage).toFixed(1) : '0.0'}%
                                                                                            </span>
                                                                                        </td>

                                                                                        <td className="px-4 py-2.5 text-center">
                                                                                            <span className={`inline-block font-black px-2 py-0.5 rounded text-[10px] ${
                                                                                                member.avg_completion >= 75 ? 'text-emerald-700 bg-emerald-50' :
                                                                                                member.avg_completion >= 40 ? 'text-amber-700 bg-amber-50' : 'text-rose-700 bg-rose-50'
                                                                                            }`}>
                                                                                                {member.avg_completion}%
                                                                                            </span>
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
 
                                                                    {/* Pagination Controls */}
                                                                    {totalPages > 1 && (
                                                                        <div className="flex flex-col sm:flex-row justify-between items-center px-4 py-3 bg-white border-t border-slate-100 gap-3" onClick={(e) => e.stopPropagation()}>
                                                                            <span className="text-[11px] font-bold text-slate-400">
                                                                                Showing <span className="text-slate-700">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
                                                                                <span className="text-slate-700">{Math.min(currentPage * itemsPerPage, filteredTeam.length)}</span> of{" "}
                                                                                <span className="text-slate-700">{filteredTeam.length}</span> members
                                                                            </span>
                                                                            <div className="flex items-center gap-1.5">
                                                                                <button
                                                                                    disabled={currentPage === 1}
                                                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                                    className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                                                    title="Previous Page"
                                                                                >
                                                                                    <FiChevronLeft size={16} />
                                                                                </button>
                                                                                
                                                                                {(() => {
                                                                                    let startPage = currentPage;
                                                                                    if (totalPages <= 3) {
                                                                                        startPage = 1;
                                                                                    } else {
                                                                                        if (currentPage + 2 > totalPages) {
                                                                                            startPage = totalPages - 2;
                                                                                        }
                                                                                    }
                                                                                    const visibleLength = Math.min(3, totalPages);
                                                                                    return Array.from({ length: visibleLength }, (_, idx) => startPage + idx).map(pageNum => (
                                                                                        <button
                                                                                            key={pageNum}
                                                                                            onClick={() => setCurrentPage(pageNum)}
                                                                                            className={`w-9 h-9 flex items-center justify-center text-xs font-bold rounded-xl transition-all ${
                                                                                                currentPage === pageNum
                                                                                                    ? 'bg-red-600 text-white shadow-sm'
                                                                                                    : 'border border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
                                                                                            }`}
                                                                                        >
                                                                                            {pageNum}
                                                                                        </button>
                                                                                    ));
                                                                                })()}
 
                                                                                <button
                                                                                    disabled={currentPage === totalPages}
                                                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                                                    className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                                                    title="Next Page"
                                                                                >
                                                                                    <FiChevronRight size={16} />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                        </div>
                    </div>
                )}
            </div>
             {/* Detail Popup Modal */}
            <AnimatePresence>
                {popupData && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 md:p-6" onClick={() => setPopupData(null)}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            onClick={(e) => e.stopPropagation()}
                            className={`bg-white w-full rounded-3xl shadow-2xl overflow-hidden flex flex-col border border-slate-100 max-h-[90vh] ${
                                popupData.type === 'ticket' ? 'max-w-6xl' : 'max-w-4xl'
                            }`}
                        >
                            {/* Modal Header */}
                            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm ${
                                        popupData.type === 'timesheet' 
                                            ? 'bg-blue-50 text-blue-600 border border-blue-100' 
                                            : 'bg-rose-50 text-rose-600 border border-rose-100'
                                    }`}>
                                        {popupData.type === 'timesheet' ? <FiClock size={20} /> : <FiCheckCircle size={20} />}
                                    </div>
                                    <div>
                                        {popupData.type === 'ticket' && (
                                            <span className="text-[10px] font-black uppercase text-red-600 tracking-wider block">TICKETS</span>
                                        )}
                                        <h3 className="font-bold text-slate-800 text-base">
                                            {popupData.type === 'timesheet' ? 'Timesheet Log Details' : `${popupData.memberName} — Ticket Dashboard`}
                                        </h3>
                                        <p className="text-xs font-semibold text-slate-500 mt-0.5">
                                            {popupData.type === 'timesheet' ? `${popupData.memberName} • ` : ''}<span className="text-slate-400 font-medium">{popupData.designation}</span>
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setPopupData(null)}
                                    className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <FiX size={18} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 overflow-y-auto">
                                {(() => {
                                    const filteredItems = popupData.items.filter(item => {
                                        if (!modalSearch) return true;
                                        const term = modalSearch.toLowerCase();
                                        if (popupData.type === 'timesheet') {
                                            return (
                                                (item.work_done || '').toLowerCase().includes(term) ||
                                                (item.task_ref || '').toLowerCase().includes(term) ||
                                                (item.task_type || '').toLowerCase().includes(term) ||
                                                (item.date || '').toLowerCase().includes(term)
                                            );
                                        } else {
                                            return (
                                                String(item.id).toLowerCase().includes(term) ||
                                                (item.title || '').toLowerCase().includes(term) ||
                                                (item.status || '').toLowerCase().includes(term) ||
                                                (item.priority || '').toLowerCase().includes(term) ||
                                                (item.customer_code || '').toLowerCase().includes(term) ||
                                                (item.assigned_by || '').toLowerCase().includes(term)
                                            );
                                        }
                                    });

                                    const designationLower = (popupData.designation || '').toLowerCase();
                                    const isDeveloper = designationLower.includes('programmer') || 
                                                        designationLower.includes('developer') || 
                                                        designationLower.includes('development') || 
                                                        designationLower.includes('architect');

                                    return (
                                        <>
                                            {popupData.type === 'ticket' && (
                                                <>
                                                    {/* Summary Cards */}
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                                        <div className="rounded-2xl border border-red-250 bg-red-50/20 p-4 shadow-sm">
                                                            <span className="text-[10px] font-black uppercase text-red-550 tracking-wider block">TOTAL</span>
                                                            <span className="text-2xl font-black text-red-600 mt-1 block">
                                                                {popupData.items.length}
                                                            </span>
                                                        </div>
                                                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">IN PROGRESS</span>
                                                            <span className="text-2xl font-black text-slate-800 mt-1 block">
                                                                {popupData.items.filter(item => {
                                                                    const s = (item.status || '').toLowerCase();
                                                                    return s === 'in process' || s === 'in progress' || s === 'testing' || s === 'bug reported' || s === 'testing rejected' || s.includes('pending') || s === 'on hold';
                                                                }).length}
                                                            </span>
                                                        </div>
                                                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">COMPLETED</span>
                                                            <span className="text-2xl font-black text-slate-800 mt-1 block">
                                                                {popupData.items.filter(item => {
                                                                    const s = (item.status || '').toLowerCase();
                                                                    return s === 'completed' || s.includes('approved') || s === 'testing approved';
                                                                }).length}
                                                            </span>
                                                        </div>
                                                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">CLOSED</span>
                                                            <span className="text-2xl font-black text-slate-800 mt-1 block">
                                                                {popupData.items.filter(item => (item.status || '').toLowerCase() === 'closed').length}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Table Title and Search Row */}
                                                    <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                                                        <h4 className="text-sm font-black text-slate-800 self-start sm:self-center">Tickets</h4>
                                                        <div className="relative w-full sm:w-64">
                                                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                                            <input
                                                                type="text"
                                                                placeholder="Search tickets..."
                                                                value={modalSearch}
                                                                onChange={(e) => setModalSearch(e.target.value)}
                                                                className="w-full pl-9 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:border-red-500 focus:outline-none transition-all"
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {popupData.type === 'timesheet' && (
                                                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                                                    <h4 className="text-sm font-black text-slate-800 self-start sm:self-center">Timesheet Entries</h4>
                                                    <div className="relative w-full sm:w-64">
                                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                                        <input
                                                            type="text"
                                                            placeholder="Search logs..."
                                                            value={modalSearch}
                                                            onChange={(e) => setModalSearch(e.target.value)}
                                                            className="w-full pl-9 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:border-red-500 focus:outline-none transition-all"
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {filteredItems.length === 0 ? (
                                                <div className="text-center py-16 text-slate-400 text-xs font-medium">
                                                    No logs or entries found for this selection.
                                                </div>
                                            ) : (
                                                <div className="border border-slate-100 rounded-2xl bg-white shadow-sm overflow-x-auto overflow-y-auto max-h-[45vh] custom-scrollbar">
                                                    <table className={`w-full text-left border-collapse ${popupData.type === 'timesheet' ? 'min-w-[650px]' : 'min-w-[850px]'}`}>
                                                        <thead>
                                                            <tr className="border-b border-slate-100 text-[10px] uppercase text-slate-400 font-black tracking-wider bg-slate-50/30">
                                                                {popupData.type === 'timesheet' ? (
                                                                    <>
                                                                        <th className="px-4 py-3 font-black">Date</th>
                                                                        <th className="px-4 py-3 font-black">Type</th>
                                                                        <th className="px-4 py-3 font-black">Reference / Code</th>
                                                                        <th className="px-4 py-3 font-black">Work Done</th>
                                                                        <th className="px-4 py-3 font-black text-center">Hours</th>
                                                                        <th className="px-4 py-3 text-center font-black">Completion</th>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <th className="px-4 py-3 font-black">Ticket #</th>
                                                                        <th className="px-4 py-3 font-black">Title</th>
                                                                        <th className="px-4 py-3 font-black">Status</th>
                                                                        <th className="px-4 py-3 font-black">Priority</th>
                                                                        <th className="px-4 py-3 font-black">Assigned</th>
                                                                        <th className="px-4 py-3 font-black">Completed</th>
                                                                        <th className="px-4 py-3 text-center font-black">Hours</th>
                                                                        <th className="px-4 py-3 text-center font-black">Completion</th>
                                                                        <th className="px-4 py-3 font-black">Time Taken</th>
                                                                        
                                                                        <th className="px-4 py-3 font-black">Assigned By</th>
                                                                    </>
                                                                )}
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 text-xs text-slate-600">
                                                            {filteredItems.map((item, idx) => {
                                                                if (popupData.type === 'timesheet') {
                                                                    return (
                                                                        <tr key={item.id || idx} className="hover:bg-slate-50/30 transition-colors">
                                                                            <td className="px-4 py-3 whitespace-nowrap font-semibold text-slate-505">
                                                                                {formatDateDDMMYYYY(item.date)}
                                                                            </td>
                                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                                <span className="px-2 py-0.5 rounded font-black text-[10px] uppercase border bg-slate-50 text-slate-600 border-slate-200">
                                                                                    {item.task_type || '—'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-3 max-w-xs truncate" title={item.task_ref}>
                                                                                {item.ticket_id && Number(item.ticket_id) > 0 ? (
                                                                                    <button
                                                                                        onClick={() => handleTicketClick(item.ticket_id, filteredItems)}
                                                                                        className="text-indigo-600 hover:underline font-black cursor-pointer bg-transparent border-0 p-0 text-left focus:outline-none"
                                                                                    >
                                                                                        {item.task_ref}
                                                                                    </button>
                                                                                ) : (
                                                                                    <span className="font-semibold text-slate-500">
                                                                                        {item.task_ref}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-slate-600 max-w-md break-words">
                                                                                {item.work_done}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-center font-black text-slate-700">
                                                                                {parseFloat(Number(item.hours).toFixed(1))}h
                                                                            </td>
                                                                            <td className="px-4 py-3 text-center">
                                                                                <span className={`inline-block font-black px-2 py-0.5 rounded text-[10px] ${
                                                                                    item.completion_percentage >= 75 ? 'text-emerald-700 bg-emerald-50' :
                                                                                    item.completion_percentage >= 40 ? 'text-amber-700 bg-amber-50' : 'text-rose-700 bg-rose-50'
                                                                                }`}>
                                                                                    {item.completion_percentage}%
                                                                                </span>
                                                                                
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <tr key={item.id || idx} className="hover:bg-slate-50/30 transition-colors">
                                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                                <button
                                                                                    onClick={() => handleTicketClick(item.id, filteredItems)}
                                                                                    className="text-red-500 hover:underline font-black cursor-pointer bg-transparent border-0 p-0 focus:outline-none"
                                                                                >
                                                                                    #{item.id}
                                                                                </button>
                                                                            </td>
                                                                            <td className="px-4 py-3 font-semibold text-slate-750 min-w-[200px] max-w-xs break-words" title={item.title}>
                                                                                {item.title}
                                                                            </td>
                                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                                <span className={`px-2 py-0.5 rounded font-black text-[10px] uppercase border ${getStatusBadgeClass(item.status)}`}>
                                                                                    {item.status || '—'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                                <span className={`px-2 py-0.5 rounded font-black text-[10px] uppercase border ${getPriorityBadgeClass(item.priority)}`}>
                                                                                    {item.priority || '—'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-3 whitespace-nowrap text-slate-500 font-medium">
                                                                                {item.created_date ? formatDateDDMMYYYY(item.created_date.split(' ')[0]) : '—'}
                                                                            </td>
                                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                                {item.completed_date ? (
                                                                                    <span className="px-2 py-0.5 rounded-lg font-black text-[9px] uppercase border bg-emerald-50 text-emerald-700 border-emerald-250">
                                                                                        Completed ({formatDateDDMMYYYY(item.completed_date.split(' ')[0])})
                                                                                    </span>
                                                                                ) : (item.status || '').toLowerCase() === 'new' || (item.status || '').toLowerCase() === 'open' || (item.status || '').toLowerCase() === 'reopen' ? (
                                                                                    <span className="px-2 py-0.5 rounded-lg font-black text-[9px] uppercase border bg-slate-50 text-slate-500 border-slate-200">
                                                                                        Pending
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="px-2 py-0.5 rounded-lg font-black text-[9px] uppercase border bg-amber-50 text-amber-700 border-amber-250">
                                                                                        In Progress
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-center whitespace-nowrap font-black text-slate-700">
                                                                                {item.hours ? parseFloat(Number(item.hours).toFixed(1)) + 'h' : '0h'}
                                                                            </td>
                                                                            <td className="px-4 py-3 text-center">
                                                                                <span className={`inline-block font-black px-2 py-0.5 rounded text-[10px] ${
                                                                                    Number(item.completion_percentage || 0) >= 75 ? 'text-emerald-700 bg-emerald-50' :
                                                                                    Number(item.completion_percentage || 0) >= 40 ? 'text-amber-700 bg-amber-50' : 'text-rose-700 bg-rose-50'
                                                                                }`}>
                                                                                    {item.completion_percentage || 0}%
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-3 whitespace-nowrap text-slate-600 font-medium">
                                                                                {formatTimeTaken(item.created_date, item.completed_date)}
                                                                            </td>
                                                                            
                                                                            <td className="px-4 py-3 whitespace-nowrap text-slate-500 font-semibold uppercase text-[10px] tracking-tight">
                                                                                {item.assigned_by || '—'}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                }
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            {/* Modal Footer */}
                                            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                                                <div className="flex items-center gap-4 text-xs font-semibold text-slate-500">
                                                    <span>
                                                        Total: <span className="text-slate-800 font-bold">{filteredItems.length}</span> {popupData.type === 'timesheet' ? 'Logs' : 'Tickets'}
                                                    </span>
                                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                                                    <span>
                                                        Hours: <span className="text-slate-800 font-bold">
                                                            {parseFloat(filteredItems.reduce((sum, task) => sum + Number(task.hours || 0), 0).toFixed(1))}h
                                                        </span>
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => setPopupData(null)}
                                                    className="px-4 py-2 bg-slate-850 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </motion.div>
                    </div>
                )}

                {kpiPopupData && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 md:p-6" onClick={() => setKpiPopupData(null)}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50 shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center shadow-inner">
                                        {kpiPopupData.type === 'absentees' ? <FiUserX size={20} /> : kpiPopupData.type === 'repeats' ? <FiRefreshCcw size={20} /> : <FiLogOut size={20} />}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                                            {kpiPopupData.title}
                                        </h3>
                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mt-0.5">
                                            {kpiPopupData.type === 'efficiency_trend' ? 'Efficiency Trend' : `${(kpiPopupData.items || []).length} ${(kpiPopupData.items || []).length === 1 ? 'Record' : 'Records'} Found`}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setKpiPopupData(null)}
                                    className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                                >
                                    <FiX size={20} />
                                </button>
                            </div>

                            {/* Search and Sort Toolbar */}
                            {kpiPopupData.type === 'efficiency_trend' ? (
                                <div className="px-6 py-3 border-b border-slate-100 bg-white flex flex-col sm:flex-row gap-3 items-center justify-end shrink-0 overflow-visible">
                                    <div className="flex items-center gap-2">
                                        <div className="min-w-[180px] w-auto max-w-[300px]">
                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                value={trendSelectedMonths}
                                                onChange={(selected) => setTrendSelectedMonths(selected || [])}
                                                options={monthOptions}
                                                placeholder="All Months"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '34px',
                                                        borderRadius: '12px',
                                                        borderColor: '#e2e8f0',
                                                        backgroundColor: '#ffffff',
                                                        boxShadow: 'none',
                                                        cursor: 'pointer',
                                                        '&:hover': { borderColor: '#cbd5e1' }
                                                    }),
                                                    placeholder: (base) => ({
                                                        ...base,
                                                        color: '#1e3a8a',
                                                        fontWeight: 700,
                                                        fontSize: '12px'
                                                    }),
                                                    multiValue: (base) => ({
                                                        ...base,
                                                        backgroundColor: '#eff6ff',
                                                        borderRadius: '6px',
                                                        margin: '2px',
                                                    }),
                                                    multiValueLabel: (base) => ({
                                                        ...base,
                                                        color: '#1e3a8a',
                                                        fontWeight: 700,
                                                        fontSize: '11px',
                                                        padding: '2px 6px'
                                                    }),
                                                    multiValueRemove: (base) => ({
                                                        ...base,
                                                        color: '#94a3b8',
                                                        ':hover': { color: '#ef4444', backgroundColor: '#fee2e2' },
                                                        borderRadius: '0 6px 6px 0',
                                                    }),
                                                    dropdownIndicator: (base) => ({
                                                        ...base,
                                                        color: '#1e3a8a',
                                                        padding: '4px'
                                                    }),
                                                    indicatorSeparator: () => ({ display: 'none' }),
                                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: '12px', fontWeight: 600 })
                                                }}
                                            />
                                        </div>
                                        <div className="w-28">
                                            <Select
                                                value={trendSelectedYear}
                                                onChange={(selected) => setTrendSelectedYear(selected)}
                                                options={yearOptions}
                                                placeholder="All Year"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '34px',
                                                        borderRadius: '12px',
                                                        borderColor: '#e2e8f0',
                                                        backgroundColor: '#ffffff',
                                                        boxShadow: 'none',
                                                        cursor: 'pointer',
                                                        '&:hover': { borderColor: '#cbd5e1' }
                                                    }),
                                                    placeholder: (base) => ({
                                                        ...base,
                                                        color: '#1e3a8a',
                                                        fontWeight: 700,
                                                        fontSize: '12px'
                                                    }),
                                                    singleValue: (base) => ({
                                                        ...base,
                                                        color: '#1e3a8a',
                                                        fontWeight: 700,
                                                        fontSize: '12px'
                                                    }),
                                                    dropdownIndicator: (base) => ({
                                                        ...base,
                                                        color: '#1e3a8a',
                                                        padding: '4px'
                                                    }),
                                                    indicatorSeparator: () => ({ display: 'none' }),
                                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: '12px', fontWeight: 600 })
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="px-6 py-3 border-b border-slate-100 bg-white flex flex-col sm:flex-row gap-3 items-center justify-end shrink-0">
                                    <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Sort by:</span>
                                    <div className="relative">
                                        <select
                                            value={kpiSortOption}
                                            onChange={(e) => {
                                                setKpiSortOption(e.target.value);
                                                setKpiCurrentPage(1);
                                            }}
                                            className="appearance-none bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl pl-3 pr-8 py-1.5 focus:outline-none focus:border-indigo-500 hover:border-slate-300 cursor-pointer min-w-[140px] outline-none transition-all"
                                        >
                                            <option value="default">Default</option>
                                            <option value="name_asc">Name (A-Z)</option>
                                            <option value="name_desc">Name (Z-A)</option>
                                            {kpiPopupData.type === 'absentees' && <option value="days_desc">Most Days Absent</option>}
                                            {kpiPopupData.type === 'absentees' && <option value="days_asc">Least Days Absent</option>}
                                            {kpiPopupData.type === 'retention' && <option value="date_desc">Recent Relieving</option>}
                                        </select>
                                        <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                                    </div>
                                </div>
                                <div className="relative w-full sm:w-64">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                    <input
                                        type="text"
                                        placeholder="Search records..."
                                        value={kpiSearch}
                                        onChange={(e) => {
                                            setKpiSearch(e.target.value);
                                            setKpiCurrentPage(1);
                                        }}
                                        className="w-full pl-9 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:bg-white focus:border-indigo-500 focus:outline-none transition-all"
                                    />
                                </div>
                            </div>
                            )}

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                                {kpiPopupData.type === 'efficiency_trend' ? (
                                    <div className="w-full h-80 mt-2 bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex flex-col">
                                        {trendLoading && trendData.length === 0 ? (
                                            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                                                <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-transparent border-t-red-500 border-r-red-500"></div>
                                                <span className="text-[13px] font-medium text-slate-500">Loading...</span>
                                            </div>
                                        ) : trendData.length === 0 ? (
                                            <div className="flex-1 flex items-center justify-center text-slate-500 font-medium">No efficiency data found.</div>
                                        ) : (
                                            <div className={`flex-1 min-h-0 overflow-x-auto custom-scrollbar transition-opacity duration-300 ${trendLoading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                                                <div className="h-full min-w-[500px] md:min-w-0">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart data={trendData} margin={{ top: 25, right: 15, left: -15, bottom: 5 }}>
                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} interval={0} />
                                                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} domain={[0, 100]} />
                                                            <Tooltip 
                                                                cursor={{ fill: '#f8fafc' }}
                                                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                                            />
                                                            <Bar dataKey="efficiency" fill="#4f46e5" radius={[4, 4, 0, 0]} maxBarSize={40}>
                                                                <LabelList dataKey="efficiency" position="top" fill="#475569" fontSize={11} fontWeight="bold" formatter={(val) => `${val}%`} />
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (!kpiPopupData.items || kpiPopupData.items.length === 0) ? (
                                    <div className="text-center text-slate-500 py-10 font-medium">No records found for this period.</div>
                                ) : (
                                    (() => {
                                        let processedItems = [...kpiPopupData.items];
                                        
                                        // 1. Search Filter
                                        if (kpiSearch) {
                                            const term = kpiSearch.toLowerCase();
                                            processedItems = processedItems.filter(item => {
                                                if (kpiPopupData.type === 'repeats') {
                                                    return (item.title || '').toLowerCase().includes(term) || String(item.ticket_id || '').includes(term) || (item.assignees || '').toLowerCase().includes(term);
                                                }
                                                return (item.name || '').toLowerCase().includes(term) || (item.designation || '').toLowerCase().includes(term);
                                            });
                                        }

                                        // 2. Sort Logic
                                        if (kpiSortOption !== 'default') {
                                            processedItems.sort((a, b) => {
                                                if (kpiSortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
                                                if (kpiSortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
                                                if (kpiSortOption === 'days_desc') return (b.days_absent || 0) - (a.days_absent || 0);
                                                if (kpiSortOption === 'days_asc') return (a.days_absent || 0) - (b.days_absent || 0);
                                                if (kpiSortOption === 'date_desc') return new Date(b.relieving_date || 0) - new Date(a.relieving_date || 0);
                                                return 0;
                                            });
                                        }

                                        if (processedItems.length === 0) {
                                            return <div className="text-center text-slate-500 py-10 font-medium">No records match your search.</div>;
                                        }

                                        const totalKpiPages = Math.ceil(processedItems.length / kpiItemsPerPage);
                                        const paginatedKpiItems = processedItems.slice((kpiCurrentPage - 1) * kpiItemsPerPage, kpiCurrentPage * kpiItemsPerPage);

                                        return (
                                            <div className="flex flex-col h-full">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                    {paginatedKpiItems.map((item, idx) => (
                                                        <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4">
                                                            {kpiPopupData.type === 'absentees' && (
                                                                <>
                                                                    <img src={item.image || '/default-avatar.png'} alt={item.name} className="w-10 h-10 rounded-full object-cover border-2 border-slate-100 shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="font-bold text-slate-800 text-sm">{item.name}</div>
                                                                        <div className="text-xs text-slate-500 font-medium mt-0.5">{item.days_absent} {item.days_absent === 1 ? 'Day' : 'Days'} Total</div>
                                                                        {item.details && item.details.length > 0 && (
                                                                            <div className="mt-2 space-y-1">
                                                                                {item.details.map((detail, dIdx) => (
                                                                                    <div key={dIdx} className="flex items-center justify-between bg-slate-50 border border-slate-100 px-2 py-1 rounded text-[10px]">
                                                                                        <span className="font-semibold text-slate-600">{detail.date ? formatDateDDMMYYYY(detail.date) : ''}</span>
                                                                                        <span className={`font-bold ${
                                                                                            ['ABSENT', 'LOP'].includes((detail.status || '').toUpperCase()) ? 'text-rose-600' :
                                                                                            ['ON_LEAVE', 'HALF_DAY'].includes((detail.status || '').toUpperCase()) ? 'text-amber-600' :
                                                                                            'text-indigo-600'
                                                                                        }`}>
                                                                                            {(detail.status || '').replace(/_/g, ' ')}
                                                                                        </span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </>
                                                            )}
                                                            {kpiPopupData.type === 'repeats' && (
                                                                <>
                                                                    <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
                                                                        <FiRefreshCcw size={18} />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div 
                                                                            className="font-bold text-indigo-600 hover:text-indigo-800 cursor-pointer text-sm truncate"
                                                                            onClick={() => {
                                                                                setKpiPopupData(null);
                                                                                handleTicketClick(item.ticket_master_id || item.ticket_id, kpiPopupData.items);
                                                                            }}
                                                                        >
                                                                            {item.title || `Ticket #${item.ticket_id}`}
                                                                        </div>
                                                                        <div className="text-xs text-slate-500 font-medium mt-1">
                                                                            Assigned to: <span className="text-slate-700">{item.assignees || 'Unassigned'}</span>
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                            {kpiPopupData.type === 'retention' && (
                                                                <>
                                                                    <img src={item.image || '/default-avatar.png'} alt={item.name} className="w-10 h-10 rounded-full object-cover border-2 border-slate-100 shrink-0 opacity-70 grayscale" />
                                                                    <div>
                                                                        <div className="font-bold text-slate-800 text-sm">{item.name}</div>
                                                                        <div className="text-xs text-slate-500 font-medium mt-0.5">{item.designation}</div>
                                                                        <div className="text-[11px] bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded-full inline-block mt-1">
                                                                            Relieved: {item.relieving_date ? formatDateDDMMYYYY(item.relieving_date) : 'N/A'}
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Pagination Controls */}
                                                {totalKpiPages > 1 && (
                                                    <div className="flex flex-col sm:flex-row justify-between items-center pt-4 mt-auto border-t border-slate-200 gap-3">
                                                        <span className="text-[11px] font-bold text-slate-400">
                                                            Showing <span className="text-slate-700">{(kpiCurrentPage - 1) * kpiItemsPerPage + 1}</span> to{" "}
                                                            <span className="text-slate-700">{Math.min(kpiCurrentPage * kpiItemsPerPage, kpiPopupData.items.length)}</span> of{" "}
                                                            <span className="text-slate-700">{kpiPopupData.items.length}</span> records
                                                        </span>
                                                        <div className="flex items-center gap-1.5">
                                                            <button
                                                                disabled={kpiCurrentPage === 1}
                                                                onClick={() => setKpiCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                            >
                                                                <FiChevronLeft size={16} />
                                                            </button>
                                                            
                                                            {(() => {
                                                                let startPage = kpiCurrentPage;
                                                                if (totalKpiPages <= 3) {
                                                                    startPage = 1;
                                                                } else {
                                                                    if (kpiCurrentPage + 2 > totalKpiPages) {
                                                                        startPage = totalKpiPages - 2;
                                                                    }
                                                                }
                                                                const visibleLength = Math.min(3, totalKpiPages);
                                                                return Array.from({ length: visibleLength }, (_, idx) => startPage + idx).map(pageNum => (
                                                                    <button
                                                                        key={pageNum}
                                                                        onClick={() => setKpiCurrentPage(pageNum)}
                                                                        className={`w-8 h-8 flex items-center justify-center text-xs font-bold rounded-xl transition-all ${
                                                                            kpiCurrentPage === pageNum
                                                                                ? 'bg-red-600 text-white shadow-sm'
                                                                                : 'border border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
                                                                        }`}
                                                                    >
                                                                        {pageNum}
                                                                    </button>
                                                                ));
                                                            })()}

                                                            <button
                                                                disabled={kpiCurrentPage === totalKpiPages}
                                                                onClick={() => setKpiCurrentPage(prev => Math.min(prev + 1, totalKpiPages))}
                                                                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                            >
                                                                <FiChevronRight size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {selectedTicketId && (
                <div className="fixed inset-0" style={{ zIndex: 99999 }}>
                    <TicketDetails
                        ticketId={selectedTicketId}
                        priorityOptions={priorityOptions}
                        statusOptions={statusOptions}
                        userInfo={user}
                        onSuccess={() => {
                            window.dispatchEvent(new CustomEvent('ticket-updated'));
                        }}
                        onClose={() => {
                            setSelectedTicketId(null);
                            setSidebarIndex(-1);
                            setSidebarItems([]);
                        }}
                        onNext={() => {
                            if (sidebarIndex < sidebarItems.length - 1) {
                                const nextIndex = sidebarIndex + 1;
                                setSidebarIndex(nextIndex);
                                setSelectedTicketId(sidebarItems[nextIndex].ticket_id || sidebarItems[nextIndex].id);
                            }
                        }}
                        onPrevious={() => {
                            if (sidebarIndex > 0) {
                                const prevIndex = sidebarIndex - 1;
                                setSidebarIndex(prevIndex);
                                setSelectedTicketId(sidebarItems[prevIndex].ticket_id || sidebarItems[prevIndex].id);
                            }
                        }}
                        hasNext={sidebarIndex !== -1 && sidebarIndex < sidebarItems.length - 1}
                        hasPrevious={sidebarIndex !== -1 && sidebarIndex > 0}
                    />
                </div>
            )}
        </div>
    );
};

export default Verticalhead_workload_overview;